import React, { FC, useCallback, useState, useMemo, memo } from 'react';
import { Placement } from 'popper.js';
import styled from 'styled-components';
import _isEmpty from 'lodash/isEmpty';
import _find from 'lodash/find';
import { gqlMediaCount, gqlMediaTypeCodeEnum, gqlNoteTypeEnum as NOTE_TYPE } from '@minecraft.graphql-types';
import { AppType, usePhoneFormat } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import Button from '@minecraft.button';
import Icon from '@minecraft.icon';
import Card from '@minecraft.card';
import { CloudinaryPhotoProps } from '@minecraft.cloudinary-photo';
import { media } from '@minecraft.themes';
import Modal from '@minecraft.modal';
import CDContactInfo from '@minecraft.cd-profile-contact-info';
import QuickViewOverlay from '../../../ProfileView/QuickViewOverlay';
import { Directions, ID, MediaType, NoteType, SubmissionType } from '../../shared/types';
import TalentCard from './TalentCard';

const getCover = (submission: SubmissionType) => {
  return submission?.cover || submission?.profile?.cover;
};

const getProfilePhoto = (submission: SubmissionType): MediaType => {
  return submission.snapshotMedia || getCover(submission);
};

const TalentCardWrapper = styled.div<{ $noMargin?: boolean; $noWidth?: boolean }>`
  ${(props) => (props?.$noMargin ? '' : 'margin: 0 0 1rem;')}
  ${(props) => (props?.$noWidth ? '' : 'width: 100%;')}
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  ${media.sm`
    ${(props) => (props?.$noMargin ? '' : 'margin: 0 1rem 1rem 0;')}
    ${(props) => (props?.$noWidth ? '' : 'width: 11.125rem;')}
    justify-content: space-between;
  `};
`;

const CustomCard = styled(Card)`
  height: 100%;
`;

export const StyledCDContactInfo = styled(CDContactInfo)`
  font-size: 0.75rem;
  min-height: auto;
`;

const StyledSnapshotButton = styled(Button)`
  width: 100%;
  height: auto;
  margin-top: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
`;

const StyledIcon = styled(Icon)`
  width: 1.75rem;
  height: 1.75rem;
`;

const StyledSnapshotButtonText = styled.p`
  text-align: left;
  margin: 0;
`;

type TalentCardProps = {
  skipRepsShortCodes: boolean;
  isPublic?: boolean;
  showPickButton?: boolean;
  showImportPhotos?: boolean;
  showSnapshotButton?: boolean;
  showFeedback?: boolean;
  showCDContactInfo?: boolean;
  detailLinkPath?: string;
  groupTalentId?: ID;
  presentationId?: ID;
  guid?: string;
  folderGuid?: string;
  userName?: string;
  pickCount?: number;
  commentCount?: number;
  myPick?: any;
  pickedBy?: any[];
  submission: SubmissionType;
  appType?: AppType;
  onFeedbackModalOpen?: (submissionId: ID) => void;
  onViewAddInNotesModalOpen?: (submissionId: ID) => void;
  onEditAddInNotesModalOpen?: (submissionId: ID) => void;
  handleUpdateSubmission?: (data) => void;
  handleUserNameModalToggle?: () => void;
  handleDeleteSubmission?: (submissionId: number) => void;
  handleAddSubmissionSnapshot?: (submissionId: number) => void;
  handleReorder?: (direction: Directions) => void;
  fastCaptureNotes?: readonly NoteType[];
  menuSettings?: {
    isDeletable?: boolean;
    profileLink?: boolean;
    sizeSheet?: boolean;
    downloadSnapshot?: boolean;
    reorderDirections?: Directions[];
  };
  thumbnailCropSettings?: CloudinaryPhotoProps['thumbnailCropSettings'];
  tooltipPlacement?: Placement;
  className?: string;
  thumbnailSize?: [number, number];
  submissionNote?: string;
  submissionMediaCount?: gqlMediaCount[];
  accountCountryCode?: string;
  showEditRole?: (submissionId: number, roleName: string, roleId: number, originalRoleName: string) => void;
  viewOnly: boolean;
  openImportPhotoModal: (groupTalentId: ID) => void;
  selectionTagCode?: string;
  onSelectionTagChange?: (value: string) => void;
};

/** places this card is used:
 * - Casting - Project Detail => Session Detail => Group Detail => Talent Section
 * - Casting - Project Detail => Presentation Detail => Group Detail => Talent Section
 * & others, but not sure where right now, possibly Reps Package Presentations
 */
const TalentCardContainer: FC<TalentCardProps> = ({
  skipRepsShortCodes,
  isPublic,
  showPickButton,
  showImportPhotos,
  showFeedback,
  showSnapshotButton,
  showCDContactInfo = false,
  detailLinkPath,
  groupTalentId,
  presentationId,
  guid,
  folderGuid,
  userName,
  pickCount,
  commentCount,
  myPick,
  pickedBy,
  submission,
  appType,
  handleUpdateSubmission,
  handleUserNameModalToggle,
  handleDeleteSubmission,
  handleAddSubmissionSnapshot,
  onFeedbackModalOpen,
  onViewAddInNotesModalOpen,
  onEditAddInNotesModalOpen,
  menuSettings,
  thumbnailCropSettings,
  fastCaptureNotes,
  tooltipPlacement,
  className,
  thumbnailSize,
  submissionNote,
  submissionMediaCount,
  accountCountryCode,
  handleReorder,
  showEditRole,
  viewOnly,
  openImportPhotoModal,
  selectionTagCode,
  onSelectionTagChange,
}) => {
  const { t } = useTranslation();
  const formatPhone = usePhoneFormat(accountCountryCode);

  // we do some validation here to make sure:
  // each media has a mediaId
  // each media is not locked
  // each media is not archived
  const photoCount =
    submission?.media?.filter(
      (m) => m.mediaType.code === gqlMediaTypeCodeEnum.PHOTO && m?.mediaStatus?.code !== 'ARCHIVED' && !m?.isLocked
    )?.length || 0;

  const submissionId = Number(submission.id);
  const contactInfo = submission?.contactInfo || submission?.profile?.contactInfo;
  const submissionHasAddInNote = useMemo(
    () => !_isEmpty(_find(submission.profile?.notes, (note) => note?.noteType?.code === NOTE_TYPE.ADD_IN)),
    [submission]
  );
  const profilePhoto = useMemo(
    () => (appType === AppType.Reps ? getCover(submission) : getProfilePhoto(submission)),
    [appType, submission]
  );
  const snapshotKey = useMemo(() => getCover(submission)?.fileKey, [submission]);
  const [isDeleteMenuOpen, setDeleteMenuOpen] = useState<boolean>(false);
  const [isQuickViewOverlayOpen, setQuickViewOverlayOpen] = useState<boolean>(false);

  const handleDeleteModalToggle = useCallback(() => {
    setDeleteMenuOpen(!isDeleteMenuOpen);
  }, [isDeleteMenuOpen]);

  const handleQuickViewOverlayToggle = useCallback(() => {
    setQuickViewOverlayOpen(!isQuickViewOverlayOpen);
  }, [isQuickViewOverlayOpen]);

  const handleDeleteModalConfirmation = useCallback(() => {
    handleDeleteSubmission && handleDeleteSubmission(submissionId);
    setDeleteMenuOpen(!isDeleteMenuOpen);
  }, [isDeleteMenuOpen]);

  const handleViewAddInNotesModalOpen = useCallback((): void => {
    onViewAddInNotesModalOpen(submissionId);
  }, []);

  const handleEditAddInNotesModalOpen = useCallback((): void => {
    onEditAddInNotesModalOpen(submissionId);
  }, []);

  const handleSnapshotClick = useCallback(() => {
    handleAddSubmissionSnapshot(submissionId);
  }, [handleAddSubmissionSnapshot]);

  const renderContactInfo = useCallback(
    (): JSX.Element =>
      showCDContactInfo &&
      contactInfo && (
        <StyledCDContactInfo
          withIcons={false}
          isRepresented={submission.profile?.isRepresented}
          contactInfo={contactInfo}
          formatPhone={formatPhone}
        />
      ),
    [submission.profile, contactInfo, formatPhone]
  );

  const originalRoleName = submission?.role?.internalName || submission?.role?.name;
  const roleName = submission?.roleName || originalRoleName;
  const roleId = submission?.role?.id;
  const handleRoleNameEdit = useCallback(() => {
    showEditRole(submissionId, roleName, Number(roleId), originalRoleName);
  }, [submissionId, roleName, roleId, originalRoleName]);

  const stageName = submission?.profile?.stageName;

  return (
    <TalentCardWrapper
      className={className}
      $noMargin={className?.includes('cn_m')}
      $noWidth={className?.includes('cn_w')}
      data-testid={`talent-card-wrapper-${submissionId}`}
    >
      <CustomCard data-qa-id="talentCard">
        <TalentCard
          appType={appType}
          skipRepsShortCodes={skipRepsShortCodes}
          isPublic={isPublic}
          isCustomized={submission.isCustomized}
          submissionId={submissionId}
          presentationId={presentationId}
          roleGuid={submission?.role?.guid}
          projectGuid={submission?.role?.project?.guid}
          roleId={roleId}
          roleName={roleName}
          profile={submission.profile}
          profilePhoto={profilePhoto}
          snapshotKey={snapshotKey}
          showPickButton={showPickButton}
          showImportPhotos={showImportPhotos}
          disableImportPhotos={photoCount <= 0}
          showFeedback={showFeedback}
          detailLinkPath={detailLinkPath}
          groupTalentId={groupTalentId}
          folderGuid={folderGuid}
          userName={userName}
          pickCount={pickCount}
          commentCount={commentCount}
          myPick={myPick}
          pickedBy={pickedBy}
          menuSettings={menuSettings}
          thumbnailCropSettings={thumbnailCropSettings}
          fastCaptureNotes={fastCaptureNotes}
          tooltipPlacement={tooltipPlacement}
          thumbnailSize={thumbnailSize}
          submissionNote={submissionNote}
          submissionMediaCount={submissionMediaCount}
          onFeedbackModalOpen={onFeedbackModalOpen}
          submissionHasAddInNote={submissionHasAddInNote}
          handleUpdateSubmission={handleUpdateSubmission}
          handleUserNameModalToggle={handleUserNameModalToggle}
          handleDeleteModalToggle={handleDeleteModalToggle}
          handleViewAddInNotesModalOpen={handleViewAddInNotesModalOpen}
          handleEditAddInNotesModalOpen={handleEditAddInNotesModalOpen}
          handleQuickViewOverlayToggle={handleQuickViewOverlayToggle}
          handleReorder={handleReorder}
          handleRoleNameEdit={showEditRole ? handleRoleNameEdit : undefined}
          guid={guid}
          renderContactInfo={renderContactInfo}
          viewOnly={viewOnly}
          openImportPhotoModal={openImportPhotoModal}
          selectionTagCode={selectionTagCode}
          onSelectionTagChange={onSelectionTagChange}
        />
      </CustomCard>
      {!viewOnly && showSnapshotButton && stageName && (
        <StyledSnapshotButton
          role="button"
          type="button"
          outlined
          onClick={handleSnapshotClick}
          data-qa-id="set-snapshot-button"
        >
          <StyledIcon name="media-photo" inheritColor />
          <StyledSnapshotButtonText>{t('common:talentCard.snapshot.set')}</StyledSnapshotButtonText>
        </StyledSnapshotButton>
      )}
      {isDeleteMenuOpen && stageName && (
        <Modal
          isOpen
          modalSize="small"
          title={t('common:talentCardContainer.deleteMenuModal.title')}
          cancelLabel={t('common:button.cancel')}
          submitLabel={t('common:button.yesRemove')}
          handleToggleClick={handleDeleteModalToggle}
          handleSubmitClick={handleDeleteModalConfirmation}
          data-qa-id="delete-talent-modal"
        >
          {t('common:talentCardContainer.deleteMenuModal.description')}
        </Modal>
      )}
      {isQuickViewOverlayOpen && stageName && (
        <QuickViewOverlay
          guid={submission?.profile?.guid}
          roleId={submission?.role?.id}
          toggleOverlay={handleQuickViewOverlayToggle}
        />
      )}
    </TalentCardWrapper>
  );
};

export default memo(TalentCardContainer);
